export interface PageBoundaryElement {
  tagName: string;
  getBoundingClientRect(): Pick<DOMRect, "bottom" | "top">;
}

const MAX_SHORT_SECTION_PAGE_RATIO = 1 / 3;

export function keepTogetherBoundaryFitsPage(
  elementHeight: number,
  pageHeight: number,
): boolean {
  return elementHeight <= pageHeight;
}

/**
 * True when a blockquote wraps other boundary blocks, so page breaks land on
 * its leaf blocks instead of bumping the whole chunk to the next page.
 * Quote documents store indentation as nested blockquotes, so one blockquote
 * can span nearly a full page.
 * @param element - The candidate boundary element
 * @param blockSelectors - Selector matching the boundary block elements
 * @returns Whether the element defers to its inner boundaries
 */
export function isIndentContainerBlockquote(
  element: Element,
  blockSelectors: string,
): boolean {
  return (
    element.tagName === "BLOCKQUOTE" &&
    element.querySelector(blockSelectors) !== null
  );
}

export function findHeadingRunStart(
  items: PageBoundaryElement[],
  overflowIndex: number,
  overflowElement: PageBoundaryElement,
  sectionTop: number,
  scale: number,
  pageStart: number,
  pageHeight: number,
): number {
  const overflowTop =
    (overflowElement.getBoundingClientRect().top - sectionTop) / scale;
  let breakIndex = isHeading(overflowElement)
    ? overflowIndex
    : overflowIndex - 1;
  while (breakIndex >= 0 && !isHeading(items[breakIndex])) {
    breakIndex -= 1;
  }

  if (breakIndex < 0) {
    return overflowTop;
  }

  while (breakIndex > 0) {
    const precedingHeading = items[breakIndex - 1];
    if (!isHeading(precedingHeading)) {
      break;
    }
    const headingTop =
      (precedingHeading.getBoundingClientRect().top - sectionTop) / scale;
    if (headingTop <= pageStart) {
      break;
    }
    breakIndex -= 1;
  }
  const breakElement = items[breakIndex] ?? overflowElement;
  const headingRunStart =
    (breakElement.getBoundingClientRect().top - sectionTop) / scale;
  const nextHeadingIndex = items.findIndex(
    (item, index) => index > overflowIndex && isHeading(item),
  );
  const runEndIndex =
    nextHeadingIndex === -1 ? items.length - 1 : nextHeadingIndex - 1;
  const runEnd = items[runEndIndex] ?? overflowElement;
  const runBottom =
    (runEnd.getBoundingClientRect().bottom - sectionTop) / scale;
  const firstBodyIndex = items.findIndex(
    (item, index) => index >= breakIndex && !isHeading(item),
  );
  const firstBody = items[firstBodyIndex];
  const firstBodyBottom = firstBody
    ? (firstBody.getBoundingClientRect().bottom - sectionTop) / scale
    : runBottom;
  const shortSectionFitsPage =
    runBottom - headingRunStart <= pageHeight * MAX_SHORT_SECTION_PAGE_RATIO;
  const headingAndFirstBodyFitPage =
    firstBodyBottom - headingRunStart <= pageHeight;

  return headingRunStart > pageStart &&
    (shortSectionFitsPage ||
      (overflowIndex === firstBodyIndex && headingAndFirstBodyFitPage))
    ? headingRunStart
    : overflowTop;
}

function isHeading(
  element: PageBoundaryElement | undefined,
): element is PageBoundaryElement {
  return element !== undefined && /^H[1-6]$/.test(element.tagName);
}

/**
 * Fragment pagination: a section that opts into it declares how many atomic
 * units it holds and how to render a half-open range of them, and marks the
 * FIRST rendered element of each unit with `data-fp-unit="<index>"`. That
 * marker is the only thing the paginator has to know about the content: it
 * reads the markers out of the hidden measurer, turns them into per-page unit
 * ranges, and asks the section to render each range as real DOM.
 *
 * A unit that renders nothing (a section heading with no text) simply leaves
 * no marker; the index space stays continuous either way,
 * so a missing marker costs nothing.
 */

/** Attribute marking the first rendered element of a fragmentable unit. */
export const SECTION_UNIT_ATTRIBUTE = "data-fp-unit";

/**
 * Attribute marking chrome a fragment reprints on every one of its pages - the
 * column-header band of a table opting into `repeat-header`. The paginator only
 * measures it: whatever carries the marker is charged against every page after
 * the first, since the measured flow holds it once.
 */
export const REPEATED_HEADER_ATTRIBUTE = "data-fp-repeat-header";

/** A measured unit boundary: where one unit starts inside its section. */
export interface UnitBoundary {
  /** The unit's index in the section's unit space. */
  index: number;
  /** The unit's top, in layout pixels from the section's top. */
  top: number;
}

/** The half-open range of units one page renders. */
export interface UnitRange {
  start: number;
  end: number;
}

/**
 * The DOM marker naming a unit's first rendered element, which is how the
 * paginator finds where a unit starts. Nothing is stamped outside a fragmented
 * render, so a document with no cross-page table renders exactly as before.
 * @param unitIndex - The unit's index, or null when the render is not fragmented
 * @returns The marker attribute, spread onto the unit's first element
 */
export function unitMarker(unitIndex: number | null): Record<string, number> {
  return unitIndex === null ? {} : { [SECTION_UNIT_ATTRIBUTE]: unitIndex };
}

/**
 * Reads the measured unit boundaries out of a section's measurer element.
 *
 * Marker offsets come from getBoundingClientRect, which is scaled by any
 * ancestor CSS transform, while the page budget is in untransformed layout
 * pixels. The measured scale divides back out for the same reason it does in
 * the clip-window path.
 * @param sectionEl - The section's DOM element from the hidden measurer
 * @returns The unit boundaries, in document order
 */
export function readUnitBoundaries(sectionEl: HTMLElement): UnitBoundary[] {
  const totalHeight = sectionEl.offsetHeight;
  const sectionRect = sectionEl.getBoundingClientRect();
  const scale =
    totalHeight > 0 && sectionRect.height > 0
      ? sectionRect.height / totalHeight
      : 1;
  return Array.from(
    sectionEl.querySelectorAll(`[${SECTION_UNIT_ATTRIBUTE}]`),
  ).flatMap((element) => {
    if (!(element instanceof HTMLElement)) {
      return [];
    }
    const index = Number(element.getAttribute(SECTION_UNIT_ATTRIBUTE));
    if (!Number.isInteger(index)) {
      return [];
    }
    const top = (element.getBoundingClientRect().top - sectionRect.top) / scale;
    return [{ index, top }];
  });
}

/**
 * Measures the closing edge a sub-table fragment adds below its last row: the
 * split table's own bottom border plus the bottom padding and borders of every
 * wrapper between it and the section root, all of which a fragment re-renders.
 * The measured flow holds these once at the very end of the table, so a page
 * packed flush to its budget pushes them past the clip window - the closing
 * rule of a `rules="vertical"` grid vanishes at the page bottom. Computed
 * styles are layout pixels, so no transform-scale correction applies.
 * @param sectionEl - The section's DOM element from the hidden measurer
 * @returns The closing edge height in layout pixels, 0 when nothing splits
 */
export function readClosingEdgeHeight(sectionEl: HTMLElement): number {
  const marker = sectionEl.querySelector(`[${SECTION_UNIT_ATTRIBUTE}]`);
  const table = marker?.closest("table");
  if (!table) {
    return 0;
  }
  const chain: Element[] = [];
  for (
    let element: Element | null = table;
    element !== null && element !== sectionEl;
    element = element.parentElement
  ) {
    chain.push(element);
  }
  return Math.ceil(
    chain.reduce((edge, element) => {
      const styles = getComputedStyle(element);
      return (
        edge +
        (Number.parseFloat(styles.borderBottomWidth) || 0) +
        (Number.parseFloat(styles.paddingBottom) || 0)
      );
    }, 0),
  );
}

/**
 * Splits a section's units into one range per page, breaking only where a unit
 * starts. Returns null when the section cannot be fragmented at all - a single
 * unit taller than a page, or no markers to break on - so the caller falls back
 * to clip-window slicing rather than emitting a page that overflows.
 *
 * The measured flow holds any repeated chrome once, above the unit it precedes,
 * so the first page's budget already carries it. Every later page reprints it,
 * which is what `repeatedChromeHeight` charges back: those pages hold that much
 * less content, and a unit taller than what remains cannot be placed at all.
 * @param boundaries - The measured unit boundaries, in document order
 * @param totalHeight - The section's measured height, in layout pixels
 * @param unitCount - The number of units the section declares
 * @param pageHeight - Available content height per page, in layout pixels
 * @param repeatedChromeHeight - Height reprinted on every page after the first
 * @param continuationPageHeight - Content height on overflow pages, before repeated table chrome
 * @param closingEdgeHeight - The split table's measured closing edge, reserved
 * out of every page's budget so a fragment's own bottom border stays visible
 * @returns One range per page, or null when the section must not be fragmented
 */
export function computeUnitRanges(
  boundaries: UnitBoundary[],
  totalHeight: number,
  unitCount: number,
  pageHeight: number,
  repeatedChromeHeight = 0,
  closingEdgeHeight = 0,
  continuationPageHeight = pageHeight,
): UnitRange[] | null {
  if (boundaries.length === 0 || unitCount === 0) {
    return null;
  }

  const continuationHeight =
    continuationPageHeight - repeatedChromeHeight - closingEdgeHeight;
  const scan = boundaries.reduce<RangeScan>(
    (state, boundary, position) => {
      if (state.overflowed) {
        return state;
      }
      const bottom = boundaries[position + 1]?.top ?? totalHeight;
      const budget =
        state.ranges.length === 0
          ? pageHeight - closingEdgeHeight
          : continuationHeight;
      if (bottom - state.pageTop <= budget) {
        return state;
      }
      if (
        boundary.index <= state.startIndex ||
        bottom - boundary.top > continuationHeight
      ) {
        return { ...state, overflowed: true };
      }
      return {
        ranges: [
          ...state.ranges,
          { start: state.startIndex, end: boundary.index },
        ],
        pageTop: boundary.top,
        startIndex: boundary.index,
        overflowed: false,
      };
    },
    { ranges: [], pageTop: 0, startIndex: 0, overflowed: false },
  );

  if (scan.overflowed) {
    return null;
  }
  return [...scan.ranges, { start: scan.startIndex, end: unitCount }];
}

/** The state carried while scanning boundaries into per-page ranges. */
interface RangeScan {
  ranges: UnitRange[];
  /** Where the page being filled starts, in layout pixels. */
  pageTop: number;
  /** The first unit index of the page being filled. */
  startIndex: number;
  /** Set once a unit was found that no page can hold. */
  overflowed: boolean;
}
